import { Avatar, Box, List, ListItem, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, Menu, useTheme } from '@mui/material'
import { useMe } from '../../hooks/useMe'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import GlobalPopMenuList from '../GlobalPopMenuList'
import { useState } from 'react'
import StorageBar from './StorageBar'
import WipTooltip from '../WipTooltip'
import ConfirmLoginDialog from '../ConfirmLoginDialog'
import { is_history_enabled } from '../../utils/config'

type Props = {
  side_size: number
  pane: 2 | 3
}
const GlobalLeftbar: React.FC<Props> = ({side_size, pane}) => {
  const me = useMe()
  const {t} = useTranslation()
  const [anchor_element, setAnchorElement] = useState<null | HTMLElement>(null)
  const location = useLocation()
  const [show_login_dialog, setShowLoginDialog] = useState(false)
  const isNow = (path: string) => path === location.pathname
  const isNowIcon = (path: string) =>
    isNow(path) ? 'material-icons' : 'material-icons-outlined'
  const theme = useTheme()
  // @ts-expect-error テーマの型が不明 | やや邪悪だがミスがあれば一発でわかるためこのまま
  const header_height: string = theme.components.MuiToolbar.styleOverrides.root.height
  const is_wide = side_size > 150

  return <>
    <Box
      sx={{
        position: pane === 3 ? 'fixed' : undefined,
        // 固定時はコンテンツ（全幅タイムライン列など）より前面に。ヘッダー直下。
        zIndex: pane === 3 ? (theme => theme.zIndex.appBar - 1) : undefined,
        top: pane === 3 ? header_height : undefined,
        left: 0,
        paddingLeft: 'env(safe-area-inset-left)',
        width: side_size,
        minWidth: 0, // これがないとflexの幅がtextなどで壊れる
        height: pane === 3 ? `calc(100% - ${header_height})` : '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          paddingTop: '6px',
          marginRight: '12px',
          flex: 1, overflowY: 'auto', scrollbarWidth: 'thin',
        }}
      >
        <List
          sx={{
            '& .MuiListItem-root': {
              padding: is_wide ? '5px 18px' : '5px 0',
            },
            '& .MuiListItem-root .MuiButtonBase-root': is_wide ? {} : {
              display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 0,
            },
            '& .MuiListItem-root .MuiButtonBase-root .MuiTypography-root': is_wide ? {} : {
              fontSize: '0.7rem'
            },
            '& .MuiListItem-root .MuiListItemIcon-root': is_wide ? {} : {
              minWidth: 'auto',
            },
          }}
        >
          <ListItem style={{padding: is_wide ? undefined : '6px 0'}}>
            {me ?
              <ListItemButton
                component={Link}
                to={`/user/${me.user_id}`}
                state={{from: location.pathname}}
                sx={{
                  padding: is_wide ? undefined : '6px 13px',
                  // 縦に並べる（左揃え）
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                  width: '100%',
                }}
                draggable={false}
              >
                <ListItemAvatar>
                  <Avatar
                    alt={me.user_name} src={me.user_icon}
                    sx={{width: 58, height: 58}}
                    slotProps={{img: {draggable: false}}}
                  />
                </ListItemAvatar>
                {is_wide && <ListItemText
                  primary={me.user_name}
                  secondary={me.metamory_id}
                  sx={{
                    ml: 0.5, width: '100%', minWidth: 0,
                    wordBreak: 'break-all',
                  }}
                  slotProps={{
                    primary: {fontSize: 20},
                    secondary: {fontSize: 16},
                  }}
                />}
              </ListItemButton>
            :
              null
            }
          </ListItem>
          {FEATURE_HOME ? (
            <ListItem>
              <ListItemButton
                {...(me ? {component: Link, to: '/'} : {onClick: ()=> { setShowLoginDialog(true) }})}
                draggable={false}
              >
                <ListItemIcon>
                  <span className={isNowIcon('/')}>home</span>
                </ListItemIcon>
                <ListItemText
                  primary={t('feature.home')}
                />
              </ListItemButton>
            </ListItem>
          ) : (
            <WipTooltip>
              <ListItem>
                <ListItemButton
                  draggable={false}
                >
                  <ListItemIcon>
                    <span className={isNowIcon('/')} style={{color: 'gray'}}>home</span>
                  </ListItemIcon>
                  <ListItemText
                    primary={t('feature.home')}
                    slotProps={{primary: {color: 'gray'}}}
                  />
                </ListItemButton>
              </ListItem>
            </WipTooltip>
          )}
          {is_history_enabled && me && <ListItem>
            <ListItemButton component={Link} to='/history/worlds'>
              <ListItemIcon><span className={isNowIcon('/history/worlds')}>history</span></ListItemIcon>
              <ListItemText primary={t('history.title')}/>
            </ListItemButton>
          </ListItem>}
          <ListItem>
            <ListItemButton
              {...(me ? {component: Link, to: '/photo'} : {onClick: ()=> { setShowLoginDialog(true) }})}
              draggable={false}
            >
              <ListItemIcon>
                <span className={isNowIcon('/photo')}>photo</span>
              </ListItemIcon>
              <ListItemText primary={t('feature.my_photo')}/>
            </ListItemButton>
          </ListItem>
          {FEATURE_ALBUM && <ListItem>
            <ListItemButton
              {...(me ? {component: Link, to: '/album'} : {onClick: ()=> { setShowLoginDialog(true) }})}
              draggable={false}
            >
              <ListItemIcon>
                <span className={isNowIcon('/album')}>folder</span>
              </ListItemIcon>
              <ListItemText
                primary={t('feature.my_album')}
              />
            </ListItemButton>
          </ListItem>}
          {FEATURE_SHARE && <ListItem>
            <ListItemButton
              {...(me ? {component: Link, to: '/share'} : {onClick: ()=> { setShowLoginDialog(true) }})}
              draggable={false}
            >
              <ListItemIcon>
                <span className={isNowIcon('/share')}>
                  folder_shared
                </span>
              </ListItemIcon>
              <ListItemText
                primary={t('feature.share_album')}
              />
            </ListItemButton>
          </ListItem>}
          {FEATURE_ADDRESS_BOOK && <ListItem>
            <ListItemButton component={Link} to='/address_book' draggable={false}>
              <ListItemIcon>
                <span className={isNowIcon('/address_book')}>group</span>
              </ListItemIcon>
              <ListItemText primary={t('general.address_book')}/>
            </ListItemButton>
          </ListItem>}
          {FEATURE_DM ? (
            <ListItem>
              <ListItemButton
                {...(me ? {component: Link, to: '/dm'} : {onClick: ()=> { setShowLoginDialog(true) }})}
                draggable={false}
              >
                <ListItemIcon>
                  <span className={isNowIcon('/dm')}>
                    mail
                  </span>
                </ListItemIcon>
                <ListItemText
                  primary={t('feature.dm')}
                />
              </ListItemButton>
            </ListItem>
          ) : (
            <WipTooltip>
              <ListItem>
                <ListItemButton
                  draggable={false}
                >
                  <ListItemIcon>
                    <span className={isNowIcon('/dm')} style={{color: 'gray'}}>mail</span>
                  </ListItemIcon>
                  <ListItemText
                    primary={t('feature.dm')}
                    slotProps={{primary: {color: 'gray'}}}
                  />
                </ListItemButton>
              </ListItem>
            </WipTooltip>
          )}
          <ListItem>
            <ListItemButton
              onClick={e=> { setAnchorElement(e.currentTarget) }}
            >
              <ListItemIcon>
                <span className='material-icons-outlined'>more_horiz</span>
              </ListItemIcon>
              <ListItemText primary={t('general.other')}/>
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {me && is_wide && <List
        sx={{alignItems: 'flex-end'}}
      >
        <StorageBar me={me}/>
      </List>}
    </Box>

    <Menu
      anchorEl={anchor_element}
      open={Boolean(anchor_element)}
      onClose={()=> { setAnchorElement(null) }}
    >
      <GlobalPopMenuList
        is_mobile={false}
        close={()=> { setAnchorElement(null) }}
      />
    </Menu>

    <ConfirmLoginDialog
      open={show_login_dialog}
      onClose={()=> { setShowLoginDialog(false) }}
    />
  </>
}
export default GlobalLeftbar
